import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl mt-8 mb-2 border-l-4 border-[var(--pc-red)] pl-3">{children}</h2>
);

function ContentES() {
  return (
    <>
      <H2>1. Responsable del tratamiento</H2>
      <p>
        El responsable del tratamiento de tus datos personales es <strong>PILLAUNCOCHE S.L.</strong>, con CIF
        <strong> B-88107685</strong>, domicilio social en <strong>C/Gutiérrez Sañudo 19, 28038 Madrid</strong>,
        teléfono <strong>+34 613 80 75 14</strong> y correo electrónico de contacto{" "}
        <a className="text-[var(--pc-red)] link-hover-red" href="mailto:info@pillauncoche.com">info@pillauncoche.com</a>.
      </p>

      <H2>2. Datos que tratamos</H2>
      <p>Podemos tratar las siguientes categorías de datos:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Datos identificativos: nombre, apellidos, DNI/NIE, fecha de nacimiento.</li>
        <li>Datos de contacto: dirección postal, teléfono, correo electrónico.</li>
        <li>Datos del carnet de conducir y su fecha de expedición.</li>
        <li>Datos de pago: procesados directamente por Stripe / PayPal (no almacenamos números de tarjeta).</li>
        <li>Imágenes de DNI y carnet subidas al hacer la reserva, alojadas de forma privada en Cloudinary.</li>
        <li>Datos de navegación (cookies): ver Política de Cookies.</li>
      </ul>

      <H2>3. Finalidades y base legal</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Gestionar la reserva y el contrato de alquiler</strong> — ejecución de contrato (art. 6.1.b RGPD).</li>
        <li><strong>Facturación y obligaciones fiscales</strong> — obligación legal (art. 6.1.c RGPD).</li>
        <li><strong>Enviar el contrato y la factura por email</strong> — ejecución de contrato.</li>
        <li><strong>Comunicaciones promocionales y newsletter</strong> — consentimiento previo (art. 6.1.a RGPD), revocable en cualquier momento.</li>
        <li><strong>Prevención del fraude y verificación del carnet</strong> — interés legítimo (art. 6.1.f RGPD).</li>
      </ul>

      <H2>4. Plazos de conservación</H2>
      <p>
        Conservamos los datos durante la relación contractual y, tras su finalización, durante los plazos legales
        aplicables: <strong>6 años</strong> para documentación mercantil/fiscal (Código de Comercio y Ley General
        Tributaria) y <strong>4 años</strong> para justificantes de reservas y contratos. Los datos tratados por
        consentimiento (marketing) se conservan hasta que revoques dicho consentimiento.
      </p>

      <H2>5. Destinatarios y encargados del tratamiento</H2>
      <p>Tus datos pueden ser comunicados a los siguientes prestadores, todos con contratos de encargo del tratamiento firmados:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Stripe Payments Europe Ltd.</strong> (Irlanda) — pasarela de pago.</li>
        <li><strong>PayPal (Europe) S.à r.l.</strong> (Luxemburgo) — pasarela de pago alternativa.</li>
        <li><strong>Resend, Inc.</strong> (EE. UU.) — envío de emails transaccionales (contrato + factura).</li>
        <li><strong>Google Ireland Ltd.</strong> — autenticación (Google Sign-In).</li>
        <li><strong>MongoDB Atlas / Railway</strong> — hosting de la base de datos y aplicación.</li>
      </ul>
      <p>
        No cedemos tus datos a otros terceros salvo obligación legal (autoridades, jueces, tribunales, aseguradora
        en caso de siniestro).
      </p>

      <H2>6. Transferencias internacionales</H2>
      <p>
        Algunos encargados del tratamiento (Resend, MongoDB Atlas, Google, Stripe) pueden alojar datos fuera del
        Espacio Económico Europeo. Todas estas transferencias están cubiertas por Cláusulas Contractuales Tipo
        aprobadas por la Comisión Europea o por el marco Data Privacy Framework (EE. UU.).
      </p>

      <H2>7. Tus derechos</H2>
      <p>Puedes ejercer en cualquier momento los siguientes derechos escribiendo a <a className="text-[var(--pc-red)] link-hover-red" href="mailto:info@pillauncoche.com">info@pillauncoche.com</a>:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Acceso</strong> a tus datos.</li>
        <li><strong>Rectificación</strong> de datos inexactos.</li>
        <li><strong>Supresión</strong> (derecho al olvido).</li>
        <li><strong>Limitación</strong> del tratamiento.</li>
        <li><strong>Oposición</strong> al tratamiento.</li>
        <li><strong>Portabilidad</strong> de tus datos.</li>
        <li>Revocar el <strong>consentimiento</strong> en cualquier momento.</li>
        <li>Presentar una reclamación ante la <strong>Agencia Española de Protección de Datos</strong> (<a className="text-[var(--pc-red)] link-hover-red break-all" href="https://www.aepd.es" target="_blank" rel="noreferrer">www.aepd.es</a>).</li>
      </ul>

      <H2>8. Seguridad</H2>
      <p>
        Aplicamos medidas técnicas y organizativas apropiadas: cifrado TLS/HTTPS, hashing bcrypt de contraseñas,
        tokens de sesión con expiración, control de accesos por roles, backups periódicos y logs de auditoría.
      </p>

      <H2>9. Menores</H2>
      <p>
        No prestamos servicio a menores de 21 años (requisito de conducción). No recopilamos deliberadamente datos
        de menores.
      </p>

      <H2>10. Modificaciones</H2>
      <p>
        Nos reservamos el derecho a modificar esta política. La versión vigente estará siempre publicada en esta
        página con la fecha de última actualización.
      </p>

      <p className="text-xs text-neutral-500 border-t border-neutral-200 pt-4 mt-8">
        Última actualización: 03/07/2026. Documento orientativo; para uso en producción se recomienda validación
        por asesor legal.
      </p>
    </>
  );
}

function ContentEN() {
  return (
    <>
      <H2>1. Data controller</H2>
      <p>
        The controller responsible for processing your personal data is <strong>PILLAUNCOCHE S.L.</strong>, Tax ID
        <strong> B-88107685</strong>, registered address at <strong>C/Gutiérrez Sañudo 19, 28038 Madrid, Spain</strong>,
        phone <strong>+34 613 80 75 14</strong> and contact email{" "}
        <a className="text-[var(--pc-red)] link-hover-red" href="mailto:info@pillauncoche.com">info@pillauncoche.com</a>.
      </p>

      <H2>2. Data we process</H2>
      <p>We may process the following categories of data:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Identification data: first name, last name, national ID/NIE, date of birth.</li>
        <li>Contact data: postal address, phone number, email address.</li>
        <li>Driving licence data and its issue date.</li>
        <li>Payment data: processed directly by Stripe / PayPal (we do not store card numbers).</li>
        <li>Photos of ID and driving licence uploaded when booking, stored privately in Cloudinary.</li>
        <li>Browsing data (cookies): see our Cookie Policy.</li>
      </ul>

      <H2>3. Purposes and legal basis</H2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Managing the booking and rental contract</strong> — performance of a contract (GDPR art. 6.1.b).</li>
        <li><strong>Invoicing and tax obligations</strong> — legal obligation (GDPR art. 6.1.c).</li>
        <li><strong>Sending the contract and invoice by email</strong> — performance of a contract.</li>
        <li><strong>Promotional communications and newsletter</strong> — prior consent (GDPR art. 6.1.a), revocable at any time.</li>
        <li><strong>Fraud prevention and licence verification</strong> — legitimate interest (GDPR art. 6.1.f).</li>
      </ul>

      <H2>4. Retention periods</H2>
      <p>
        We keep your data for the duration of the contractual relationship and, after it ends, for the applicable
        legal periods: <strong>6 years</strong> for commercial/tax documentation (Commercial Code and General Tax
        Law) and <strong>4 years</strong> for booking and contract records. Data processed based on consent
        (marketing) is kept until you withdraw that consent.
      </p>

      <H2>5. Recipients and data processors</H2>
      <p>Your data may be shared with the following providers, all under signed data processing agreements:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Stripe Payments Europe Ltd.</strong> (Ireland) — payment gateway.</li>
        <li><strong>PayPal (Europe) S.à r.l.</strong> (Luxembourg) — alternative payment gateway.</li>
        <li><strong>Resend, Inc.</strong> (USA) — transactional emails (contract + invoice).</li>
        <li><strong>Google Ireland Ltd.</strong> — authentication (Google Sign-In).</li>
        <li><strong>MongoDB Atlas / Railway</strong> — database and application hosting.</li>
      </ul>
      <p>
        We do not share your data with other third parties except where legally required (authorities, judges,
        courts, insurer in the event of an accident).
      </p>

      <H2>6. International transfers</H2>
      <p>
        Some data processors (Resend, MongoDB Atlas, Google, Stripe) may host data outside the European Economic
        Area. All such transfers are covered by Standard Contractual Clauses approved by the European Commission
        or by the Data Privacy Framework (USA).
      </p>

      <H2>7. Your rights</H2>
      <p>You can exercise the following rights at any time by writing to <a className="text-[var(--pc-red)] link-hover-red" href="mailto:info@pillauncoche.com">info@pillauncoche.com</a>:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Access</strong> your data.</li>
        <li><strong>Rectify</strong> inaccurate data.</li>
        <li><strong>Erasure</strong> (right to be forgotten).</li>
        <li><strong>Restriction</strong> of processing.</li>
        <li><strong>Objection</strong> to processing.</li>
        <li><strong>Portability</strong> of your data.</li>
        <li>Withdraw your <strong>consent</strong> at any time.</li>
        <li>Lodge a complaint with the <strong>Spanish Data Protection Agency (AEPD)</strong> (<a className="text-[var(--pc-red)] link-hover-red break-all" href="https://www.aepd.es" target="_blank" rel="noreferrer">www.aepd.es</a>).</li>
      </ul>

      <H2>8. Security</H2>
      <p>
        We apply appropriate technical and organisational measures: TLS/HTTPS encryption, bcrypt password hashing,
        expiring session tokens, role-based access control, periodic backups, and audit logs.
      </p>

      <H2>9. Minors</H2>
      <p>
        We do not provide service to anyone under 21 years old (driving requirement). We do not knowingly collect
        data from minors.
      </p>

      <H2>10. Changes</H2>
      <p>
        We reserve the right to modify this policy. The current version will always be published on this page
        along with the last update date.
      </p>

      <p className="text-xs text-neutral-500 border-t border-neutral-200 pt-4 mt-8">
        Last updated: 03/07/2026. Informational document; professional legal review is recommended for production use.
      </p>
    </>
  );
}

export default function Privacidad() {
  const { i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");

  return (
    <section className="section-light py-16" data-testid="privacidad-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="divider-red mb-4"></div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">{isEn ? "Privacy Policy" : "Política de Privacidad"}</h1>
        <p className="text-xs uppercase tracking-widest text-neutral-500 mb-8">
          {isEn ? "Official document · pillauncoche S.L. · GDPR" : "Documento oficial · pillauncoche S.L. · RGPD & LOPDGDD"}
        </p>

        <article className="prose prose-neutral max-w-none text-base leading-relaxed text-neutral-800 space-y-5">
          {isEn ? <ContentEN /> : <ContentES />}
        </article>

        <div className="mt-10 flex justify-center">
          <Link to="/" className="btn-red px-8 py-3 text-sm uppercase tracking-widest" data-testid="privacidad-accept-btn">
            {isEn ? "Accept and Return Home" : "Aceptar y Volver al Inicio"}
          </Link>
        </div>
      </div>
    </section>
  );
}
